import React, { useState, useEffect } from 'react'
import { useForm, useFieldArray } from 'react-hook-form'
import { 
  FiMail, 
  FiPhone, 
  FiMapPin, 
  FiClock,
  FiInstagram,
  FiTwitter,
  FiLinkedin,
  FiMusic,
  FiPlus,
  FiTrash2,
  FiSave,
  FiVideo
} from 'react-icons/fi'
import { useContent } from '../../../hooks/useContent'
import { ContactContent } from '../../../types/content'
import { toast } from 'react-hot-toast'

interface ContactEditorProps {
  onPreview?: (data: ContactContent) => void
}

interface ContactFormData extends Omit<ContactContent, 'serviceTypes' | 'contactInfo'> {
  serviceTypes: { id: string; value: string }[]
  contactInfo: {
    email: { id: string; value: string }[]
    phone: { id: string; value: string }[]
    address: { id: string; value: string }[]
    hours: { id: string; value: string }[]
  }
}

const ContactEditor: React.FC<ContactEditorProps> = ({ onPreview }) => {
  const { content, loading, saving, saveContent } = useContent('contact')
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)

  const { register, control, handleSubmit, watch, reset } = useForm<ContactFormData>({
    defaultValues: {
      title: '',
      subtitle: '',
      description: '',
      contactInfo: {
        email: [{ id: '1', value: '' }],
        phone: [{ id: '1', value: '' }],
        address: [{ id: '1', value: '' }],
        hours: [{ id: '1', value: '' }]
      },
      socialLinks: {
        instagram: '',
        twitter: '',
        linkedin: '',
        spotify: '',
        tiktok: ''
      },
      serviceTypes: [{ id: '1', value: '' }]
    }
  })

  const { fields: serviceTypeFields, append: appendServiceType, remove: removeServiceType } = useFieldArray({
    control,
    name: 'serviceTypes'
  })

  const { fields: emailFields, append: appendEmail, remove: removeEmail } = useFieldArray({
    control,
    name: 'contactInfo.email'
  })

  const { fields: phoneFields, append: appendPhone, remove: removePhone } = useFieldArray({
    control,
    name: 'contactInfo.phone'
  })

  const { fields: addressFields, append: appendAddress, remove: removeAddress } = useFieldArray({
    control,
    name: 'contactInfo.address'
  })

  const { fields: hoursFields, append: appendHours, remove: removeHours } = useFieldArray({
    control,
    name: 'contactInfo.hours'
  })

  const watchedData = watch()

  // Load content when available
  useEffect(() => {
    if (content?.data) {
      const contactData = content.data as ContactContent
      const formData: ContactFormData = {
        title: contactData.title || '',
        subtitle: contactData.subtitle || '',
        description: contactData.description || '',
        serviceTypes: contactData.serviceTypes?.length > 0 
          ? contactData.serviceTypes.map((type, index) => ({ id: `st_${index}`, value: type || '' }))
          : [{ id: '1', value: '' }],
        contactInfo: {
          email: contactData.contactInfo?.email?.length > 0
            ? contactData.contactInfo.email.map((email, index) => ({ id: `email_${index}`, value: email || '' }))
            : [{ id: '1', value: '' }],
          phone: contactData.contactInfo?.phone?.length > 0
            ? contactData.contactInfo.phone.map((phone, index) => ({ id: `phone_${index}`, value: phone || '' }))
            : [{ id: '1', value: '' }],
          address: contactData.contactInfo?.address?.length > 0
            ? contactData.contactInfo.address.map((addr, index) => ({ id: `addr_${index}`, value: addr || '' }))
            : [{ id: '1', value: '' }],
          hours: contactData.contactInfo?.hours?.length > 0
            ? contactData.contactInfo.hours.map((hour, index) => ({ id: `hour_${index}`, value: hour || '' }))
            : [{ id: '1', value: '' }]
        },
        socialLinks: {
          instagram: contactData.socialLinks?.instagram || '',
          twitter: contactData.socialLinks?.twitter || '',
          linkedin: contactData.socialLinks?.linkedin || '',
          spotify: contactData.socialLinks?.spotify || '',
          tiktok: contactData.socialLinks?.tiktok || ''
        }
      }
      reset(formData)
      setHasUnsavedChanges(false)
    }
  }, [content, reset])

  // Watch for changes
  useEffect(() => {
    if (content?.data) {
      const currentData = JSON.stringify(watchedData)
      const originalData = JSON.stringify(content.data)
      setHasUnsavedChanges(currentData !== originalData)
    }
  }, [watchedData, content])

  // Auto-preview
  useEffect(() => {
    if (hasUnsavedChanges && onPreview) {
      const contactData: ContactContent = {
        ...watchedData,
        serviceTypes: watchedData.serviceTypes.map(item => item.value),
        contactInfo: {
          email: watchedData.contactInfo.email.map(item => item.value),
          phone: watchedData.contactInfo.phone.map(item => item.value),
          address: watchedData.contactInfo.address.map(item => item.value),
          hours: watchedData.contactInfo.hours.map(item => item.value)
        }
      }
      onPreview(contactData)
    }
  }, [watchedData, hasUnsavedChanges, onPreview])

  const onSubmit = async (data: ContactFormData) => {
    const contactData: ContactContent = {
      ...data,
      serviceTypes: data.serviceTypes.map(item => item.value),
      contactInfo: {
        email: data.contactInfo.email.map(item => item.value),
        phone: data.contactInfo.phone.map(item => item.value),
        address: data.contactInfo.address.map(item => item.value),
        hours: data.contactInfo.hours.map(item => item.value)
      }
    }
    
    const success = await saveContent(contactData)
    if (success) {
      setHasUnsavedChanges(false)
      toast.success('Contact content saved successfully!')
    } else {
      toast.error('Failed to save contact content')
    }
  }

  const handleAddServiceType = () => {
    appendServiceType({ id: `st_${Date.now()}`, value: '' })
  }

  const handleRemoveServiceType = (index: number) => {
    if (serviceTypeFields.length > 1) {
      removeServiceType(index)
    } else {
      toast.error('You must have at least one service type')
    }
  }

  const handleAddEmail = () => {
    appendEmail({ id: `email_${Date.now()}`, value: '' })
  }

  const handleAddPhone = () => {
    appendPhone({ id: `phone_${Date.now()}`, value: '' })
  }

  const handleAddAddress = () => {
    appendAddress({ id: `addr_${Date.now()}`, value: '' })
  }

  const handleAddHours = () => {
    appendHours({ id: `hour_${Date.now()}`, value: '' })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4"></div>
          <p className="text-neutral-600">Loading contact content...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-neutral-900">Contact Section Editor</h2>
          <p className="text-sm text-neutral-600">Manage contact information, form settings, and social links</p>
        </div>
        
        {hasUnsavedChanges && (
          <div className="flex items-center space-x-2 text-amber-600 text-sm">
            <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
            <span>Unsaved changes</span>
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        {/* Main Content */}
        <div className="bg-white rounded-2xl p-8 shadow-sm border border-neutral-200">
          <h3 className="text-lg font-semibold text-neutral-800 mb-6 flex items-center space-x-2">
            <FiMail size={18} />
            <span>Contact Content</span>
          </h3>
          
          <div className="space-y-6">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Section Title
              </label>
              <input
                {...register('title')}
                className="w-full px-4 py-2.5 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:border-transparent transition-all text-sm"
                placeholder="Get In Touch"
              />
            </div>

            {/* Subtitle */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Subtitle
              </label>
              <input
                {...register('subtitle')}
                className="w-full px-4 py-2.5 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:border-transparent transition-all text-sm"
                placeholder="Ready to Book Live Music for Your Event?"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Description
              </label>
              <textarea
                {...register('description')}
                rows={3}
                className="w-full px-4 py-2.5 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:border-transparent transition-all text-sm resize-none"
                placeholder="Tell us about your event and we'll help you find the perfect musicians..."
              />
            </div>
          </div>
        </div>

        {/* Service Types */}
        <div className="bg-white rounded-2xl p-8 shadow-sm border border-neutral-200">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-neutral-800 flex items-center space-x-2">
              <FiMusic size={18} />
              <span>Service Types</span>
            </h3>
            
            <button
              type="button"
              onClick={handleAddServiceType}
              className="flex items-center space-x-2 px-3 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors duration-200"
            >
              <FiPlus size={16} />
              <span>Add Service Type</span>
            </button>
          </div>

          <div className="space-y-4">
            {serviceTypeFields.map((field, index) => (
              <div key={field.id} className="flex items-center space-x-3">
                <input
                  {...register(`serviceTypes.${index}.value`)}
                  className="flex-1 px-4 py-2.5 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:border-transparent transition-all text-sm"
                  placeholder="e.g., Wedding Music, Corporate Event, etc."
                />
                <button
                  type="button"
                  onClick={() => handleRemoveServiceType(index)}
                  className="text-red-500 hover:text-red-700 transition-colors duration-200 p-2"
                >
                  <FiTrash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-white rounded-2xl p-8 shadow-sm border border-neutral-200">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-neutral-800 flex items-center space-x-2">
              <FiPhone size={18} />
              <span>Contact Information</span>
            </h3>
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              <p className="text-xs text-amber-700">
                <strong>Note:</strong> This information is not displayed on the frontend. Only social links are shown.
              </p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2 flex items-center space-x-2">
                <FiMail size={16} />
                <span>Email Addresses</span>
              </label>
              <div className="space-y-2">
                {emailFields.map((field, index) => (
                  <div key={field.id} className="flex items-center space-x-2">
                    <input
                      {...register(`contactInfo.email.${index}.value`)}
                      type="email"
                      className="flex-1 px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:border-transparent transition-all text-sm"
                      placeholder="ollie@o-live.co.uk"
                    />
                    {emailFields.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeEmail(index)}
                        className="text-red-500 hover:text-red-700 transition-colors duration-200 p-1"
                      >
                        <FiTrash2 size={14} />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={handleAddEmail}
                  className="text-primary hover:text-primary/80 transition-colors duration-200 text-sm flex items-center space-x-1"
                >
                  <FiPlus size={14} />
                  <span>Add Email</span>
                </button>
              </div>
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2 flex items-center space-x-2">
                <FiPhone size={16} />
                <span>Phone Numbers</span>
              </label>
              <div className="space-y-2">
                {phoneFields.map((field, index) => (
                  <div key={field.id} className="flex items-center space-x-2">
                    <input
                      {...register(`contactInfo.phone.${index}.value`)}
                      type="tel"
                      className="flex-1 px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:border-transparent transition-all text-sm"
                      placeholder="+44 7700 900000"
                    />
                    {phoneFields.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removePhone(index)}
                        className="text-red-500 hover:text-red-700 transition-colors duration-200 p-1"
                      >
                        <FiTrash2 size={14} />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={handleAddPhone}
                  className="text-primary hover:text-primary/80 transition-colors duration-200 text-sm flex items-center space-x-1"
                >
                  <FiPlus size={14} />
                  <span>Add Phone</span>
                </button>
              </div>
            </div>

            {/* Address */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2 flex items-center space-x-2">
                <FiMapPin size={16} />
                <span>Address</span>
              </label>
              <div className="space-y-2">
                {addressFields.map((field, index) => (
                  <div key={field.id} className="flex items-center space-x-2">
                    <input
                      {...register(`contactInfo.address.${index}.value`)}
                      className="flex-1 px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:border-transparent transition-all text-sm"
                      placeholder="Manchester, UK"
                    />
                    {addressFields.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeAddress(index)}
                        className="text-red-500 hover:text-red-700 transition-colors duration-200 p-1"
                      >
                        <FiTrash2 size={14} />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={handleAddAddress}
                  className="text-primary hover:text-primary/80 transition-colors duration-200 text-sm flex items-center space-x-1"
                >
                  <FiPlus size={14} />
                  <span>Add Address Line</span>
                </button>
              </div>
            </div>

            {/* Hours */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2 flex items-center space-x-2">
                <FiClock size={16} />
                <span>Business Hours</span>
              </label>
              <div className="space-y-2">
                {hoursFields.map((field, index) => (
                  <div key={field.id} className="flex items-center space-x-2">
                    <input
                      {...register(`contactInfo.hours.${index}.value`)}
                      className="flex-1 px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:border-transparent transition-all text-sm"
                      placeholder="Mon-Fri: 9AM-6PM GMT"
                    />
                    {hoursFields.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeHours(index)}
                        className="text-red-500 hover:text-red-700 transition-colors duration-200 p-1"
                      >
                        <FiTrash2 size={14} />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={handleAddHours}
                  className="text-primary hover:text-primary/80 transition-colors duration-200 text-sm flex items-center space-x-1"
                >
                  <FiPlus size={14} />
                  <span>Add Hours</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Social Links */}
        <div className="bg-white rounded-2xl p-8 shadow-sm border border-neutral-200">
          <h3 className="text-lg font-semibold text-neutral-800 mb-6 flex items-center space-x-2">
            <FiInstagram size={18} />
            <span>Social Media Links</span>
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2 flex items-center space-x-2">
                <FiInstagram size={16} />
                <span>Instagram</span>
                <span className="text-xs text-neutral-500">(Optional)</span>
              </label>
              <input
                {...register('socialLinks.instagram', {
                  validate: (value) => !value || /^https?:\/\/.+/.test(value) || 'Please enter a valid URL'
                })}
                type="text"
                className="w-full px-4 py-2.5 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:border-transparent transition-all text-sm"
                placeholder="https://instagram.com/o-live-music"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2 flex items-center space-x-2">
                <FiTwitter size={16} />
                <span>Twitter</span>
                <span className="text-xs text-neutral-500">(Optional)</span>
              </label>
              <input
                {...register('socialLinks.twitter', {
                  validate: (value) => !value || /^https?:\/\/.+/.test(value) || 'Please enter a valid URL'
                })}
                type="text"
                className="w-full px-4 py-2.5 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:border-transparent transition-all text-sm"
                placeholder="https://twitter.com/o-live-music"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2 flex items-center space-x-2">
                <FiLinkedin size={16} />
                <span>LinkedIn</span>
                <span className="text-xs text-neutral-500">(Optional)</span>
              </label>
              <input
                {...register('socialLinks.linkedin', {
                  validate: (value) => !value || /^https?:\/\/.+/.test(value) || 'Please enter a valid URL'
                })}
                type="text"
                className="w-full px-4 py-2.5 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:border-transparent transition-all text-sm"
                placeholder="https://linkedin.com/company/o-live-music"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2 flex items-center space-x-2">
                <FiMusic size={16} />
                <span>Spotify</span>
                <span className="text-xs text-neutral-500">(Optional)</span>
              </label>
              <input
                {...register('socialLinks.spotify', {
                  validate: (value) => !value || /^https?:\/\/.+/.test(value) || 'Please enter a valid URL'
                })}
                type="text"
                className="w-full px-4 py-2.5 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:border-transparent transition-all text-sm"
                placeholder="https://open.spotify.com/artist/o-live-music"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2 flex items-center space-x-2">
                <FiVideo size={16} />
                <span>TikTok</span>
                <span className="text-xs text-neutral-500">(Optional)</span>
              </label>
              <input
                {...register('socialLinks.tiktok', {
                  validate: (value) => !value || /^https?:\/\/.+/.test(value) || 'Please enter a valid URL'
                })}
                type="text"
                className="w-full px-4 py-2.5 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:border-transparent transition-all text-sm"
                placeholder="https://tiktok.com/@o-live-music"
              />
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="flex items-center space-x-2 px-6 py-3 bg-neutral-900 text-white rounded-lg hover:bg-neutral-800 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <div className="loading-spinner w-4 h-4"></div>
                <span>Saving...</span>
              </>
            ) : (
              <>
                <FiSave size={16} />
                <span>Save Changes</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}

export default ContactEditor
